import { Search, Plus, ImageIcon, GripVertical, Upload, Trash2, FileCode, Info } from 'lucide-react';
import { useAppContext } from '../context/AppContext';

export default function SpecsSection() {
  const {
    activeSpecCategory,
    setActiveSpecCategory,
    searchQuery,
    setSearchQuery,
    showSearchResults,
    setShowSearchResults,
    filteredCatalog,
    browsableCatalog,
    specItems,
    draggedIndex,
    handleAddSpecItemFromCatalog,
    handleAddCustomSpecItem,
    handleUpdateSpecItem,
    handleRemoveSpecItem,
    handleTriggerUpload,
    handleTriggerXmlImport,
    handleCatalogBrowserImageUpload,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    userRole,
    readOnlyState,
    proposalType,
    units,
    activeUnitId,
  } = useAppContext();

  const activeUnitLabel = proposalType === 'bulk' ? units.find(u => u.id === activeUnitId)?.label : undefined;

  return (
    <div className="bg-white p-6 rounded-xl border border-stone-200/80 shadow-card space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-stone-100 pb-4 gap-4">
        <div className="flex items-center space-x-2 text-stone-800 font-semibold">
          <Search className="w-4 h-4 text-brand-600" />
          <span className="text-xs uppercase tracking-wider font-semibold text-stone-700">Teklif Ürün Detayları (Katalog & Görsel Listesi)</span>
          {activeUnitLabel && (
            <span className="text-[10px] font-semibold uppercase tracking-wider text-brand-600 bg-brand-50/80 border border-brand-200/60 px-2.5 py-0.5 rounded-full">
              {activeUnitLabel}
            </span>
          )}
        </div>

        <div className="flex flex-wrap bg-stone-100/80 p-1 rounded-lg border border-stone-200/60 text-[11px] gap-1">
          {['Aksesuar', 'Aydınlatma', 'Kulp', 'Tezgâh', 'Renk', 'Cihaz', 'Eviye', 'Armatür'].map(cat => (
            <button
              key={cat}
              onClick={() => {
                setActiveSpecCategory(cat);
                setSearchQuery('');
                setShowSearchResults(false);
              }}
              className={`px-2.5 py-1 font-medium rounded-md transition-all ${
                activeSpecCategory === cat 
                  ? 'bg-white text-stone-900 shadow-xs font-semibold' 
                  : 'text-stone-500 hover:text-stone-900'
              }`}
            >
              {cat === 'Renk' ? 'Kapak Rengi' : cat}
            </button>
          ))}
        </div>
      </div>

      {/* Autocomplete Search */}
      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-stone-400" />
            <input 
              type="text" 
              placeholder="Katalog ürün ismi veya kod ile arayın..."
              disabled={readOnlyState.isReadOnly}
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => setShowSearchResults(true)}
              className="w-full pl-9 pr-4 py-2 text-xs rounded-lg border border-stone-200/90 focus:outline-none focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 bg-white disabled:bg-stone-50 disabled:cursor-not-allowed placeholder:text-stone-400 font-medium text-stone-800 transition-all"
            />
          </div>
          <button
            onClick={handleAddCustomSpecItem}
            disabled={readOnlyState.isReadOnly}
            className="bg-brand-50/80 hover:bg-brand-100 text-brand-700 border border-brand-200/60 px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Manuel Satır Ekle</span>
          </button>
          <button
            onClick={handleTriggerXmlImport}
            disabled={readOnlyState.isReadOnly}
            className="bg-emerald-50/80 hover:bg-emerald-100 text-emerald-700 border border-emerald-200/80 px-3.5 py-2 rounded-lg text-xs font-semibold flex items-center space-x-1.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            title="CAD / 3D Mutfak Çizim Projesi (Adeko, Winner XML) Yükle"
          >
            <FileCode className="w-3.5 h-3.5 text-emerald-600" />
            <span>XML Proje Yükle</span>
          </button>
        </div>

        {/* Autocomplete Suggestions */}
        {showSearchResults && searchQuery.trim() && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setShowSearchResults(false)} />
            <div className="absolute left-0 right-0 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-20 divide-y divide-stone-100">
              {filteredCatalog.length > 0 ? (
                filteredCatalog.map((item, idx) => (
                  <button
                    key={`${item.category}-${item.code}-${idx}`}
                    onClick={() => handleAddSpecItemFromCatalog(item)}
                    className="w-full text-left px-4 py-2.5 hover:bg-stone-50 flex items-center justify-between transition-all"
                  >
                    <div className="flex items-center space-x-3">
                      {item.image ? (
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className={`w-8 h-8 rounded border border-stone-105 bg-white ${
                            item.category === 'Renk' || item.category === 'Gövde Rengi' ? 'object-cover' : 'object-contain'
                          }`}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-stone-100 rounded border border-stone-105 flex items-center justify-center text-stone-400">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                      <div>
                        <div className="text-xs font-bold text-stone-800">{item.code}</div>
                        <div className="text-xs text-stone-500 truncate max-w-[320px]">{item.name}</div>
                      </div>
                    </div>
                    <span className="text-[9px] bg-stone-100 text-stone-600 px-2 py-0.5 rounded font-bold uppercase">{item.category === 'Renk' ? 'Kapak Rengi' : item.category}</span>
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-xs text-stone-400 text-center">
                  Kütüphanede eşleşen ürün bulunamadı.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="flex items-start space-x-2.5 text-amber-900 text-xs leading-relaxed border-l-2 border-amber-500 pl-3.5 py-2.5 bg-amber-50/50 rounded-r-lg">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600" />
        <span className="font-normal text-stone-700">
          <strong className="font-semibold text-amber-900">Bilgi:</strong> Cihaz, Eviye, Armatür ve Tezgâh kategorilerindeki ürün görsel ve detayları açık kaynaklardan alınmıştır. Lütfen ürünün stok ve teknik bilgisini üreticiyle, uygulanabilirliğini ise ÜRGE ekibiyle teyit ediniz.
        </span>
      </div>

      {/* Visual Catalog Browser */}
      {browsableCatalog.length > 0 && (
        <div className="border border-stone-200/80 rounded-lg p-3 bg-stone-50/50 space-y-2">
          <div className="text-[10px] font-bold text-stone-400 uppercase tracking-wider flex justify-between">
            <span>Mevcut Katalog Öğeleri Gözat ({activeSpecCategory === 'Renk' ? 'Kapak Rengi' : activeSpecCategory})</span>
            <span>{browsableCatalog.length} ürün listeleniyor</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
            {browsableCatalog.map((catItem, idx) => {
              const isAdded = specItems.some(i => i.code === catItem.code);
              return (
                <div 
                  key={`${catItem.category}-${catItem.code}-${idx}`} 
                  onClick={() => !isAdded && !readOnlyState.isReadOnly && handleAddSpecItemFromCatalog(catItem)}
                  className={`border rounded-xl p-2.5 flex items-center justify-between bg-white transition-all duration-300 select-none ${
                    isAdded || readOnlyState.isReadOnly
                      ? 'opacity-50 border-stone-200 bg-stone-50 cursor-not-allowed' 
                      : 'border-stone-250 hover:border-brand-400 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 cursor-pointer'
                  }`}
                >
                  <div className="flex items-center space-x-3 overflow-hidden flex-1">
                    <div className="relative group/cardimg flex-shrink-0">
                      {catItem.image ? (
                        <img 
                          src={catItem.image} 
                          alt={catItem.name} 
                          className={`w-11 h-11 rounded-lg border border-stone-100 bg-white ${
                            catItem.category === 'Renk' || catItem.category === 'Gövde Rengi' ? 'object-cover' : 'object-contain'
                          }`}
                          onError={(e) => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48"><rect width="48" height="48" fill="%23f5ebe4"/></svg>'; }}
                        />
                      ) : (
                        <div className="w-11 h-11 bg-stone-100 rounded-lg border border-stone-150 flex flex-col items-center justify-center text-stone-400">
                          <ImageIcon className="w-4 h-4" />
                        </div>
                      )}
                      
                      {/* Upload overlay button (Only for Admin & Catalog Manager) */}
                      {!readOnlyState.isReadOnly && (userRole === 'admin' || userRole === 'catalog_manager') && (
                        <>
                          <label
                            htmlFor={`browser-img-input-${idx}-${catItem.code}`}
                            onClick={(e) => e.stopPropagation()}
                            className="absolute inset-0 bg-black/60 text-white rounded-lg flex flex-col items-center justify-center opacity-0 group-hover/cardimg:opacity-100 transition-opacity cursor-pointer z-10"
                            title="Bu Ürün İçin Kataloğa Görsel Yükle"
                          >
                            <Upload className="w-3.5 h-3.5" />
                            <span className="text-[7px] font-bold mt-0.5">Yükle</span>
                          </label>
                          <input
                            type="file"
                            id={`browser-img-input-${idx}-${catItem.code}`}
                            accept="image/*"
                            className="hidden"
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => {
                              e.stopPropagation();
                              const file = e.target.files?.[0];
                              if (file) {
                                handleCatalogBrowserImageUpload(catItem.code, file);
                              }
                              e.target.value = '';
                            }}
                          />
                        </>
                      )}
                    </div>

                    <div className="overflow-hidden flex-1">
                      <div className="text-[10px] font-extrabold text-stone-900 tracking-tight truncate" title={catItem.code}>{catItem.code}</div>
                      <div className="text-[9px] text-stone-500 font-medium truncate mt-0.5" title={catItem.name}>{catItem.name.replace('Lütfen yerleşim bilgisi giriniz', '')}</div>
                    </div>
                  </div>
                  <div className="pl-1.5 flex-shrink-0">
                    {isAdded ? (
                      <span className="text-[8px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-bold">Eklendi</span>
                    ) : (
                      <span className="text-[8px] bg-brand-50 hover:bg-brand-100 text-brand-700 px-1.5 py-0.5 rounded font-bold border border-brand-200/50">Ekle</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Specification Table */}
      <div className="overflow-x-auto border border-stone-100 rounded-lg">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-stone-50/70 border-b border-stone-100 text-stone-400 text-[10px] uppercase font-bold tracking-wider">
              <th className="py-2.5 px-3 w-8"></th>
              <th className="py-2.5 px-3 w-16">Görsel</th>
              <th className="py-2.5 px-3 w-40">Alt Kategori</th>
              <th className="py-2.5 px-3 w-36">Ürün Kodu</th>
              <th className="py-2.5 px-3">Ürün İsmi / Açıklama</th>
              <th className="py-2.5 px-3 w-16 text-center">Adet</th>
              <th className="py-2.5 px-3 w-10 text-center"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {specItems.map((item, index) => (
              <tr 
                key={item.id}
                draggable={!readOnlyState.isReadOnly}
                onDragStart={() => handleDragStart(index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`hover:bg-stone-50/30 transition-all ${
                  draggedIndex === index ? 'opacity-40 bg-stone-100/50' : ''
                }`}
              >
                <td className="py-2 px-1 text-center cursor-grab active:cursor-grabbing text-stone-300 hover:text-stone-500 transition-colors">
                  <GripVertical className="w-3.5 h-3.5 mx-auto" />
                </td>
                <td className="py-2 px-3">
                  <div className="relative group w-12 h-12 rounded border border-stone-150 bg-stone-50 overflow-hidden flex items-center justify-center">
                    {item.image ? (
                      <>
                        <img 
                          src={item.image} 
                          alt={item.name} 
                          className={`w-full h-full ${item.category === 'Renk' || item.category === 'Gövde Rengi' ? 'object-cover' : 'object-contain'}`} 
                        />
                        {!readOnlyState.isReadOnly && (
                          <button
                            onClick={() => handleTriggerUpload(item.id)}
                            className="absolute inset-0 bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Görseli Değiştir"
                          >
                            <Upload className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    ) : (
                      !readOnlyState.isReadOnly && (
                        <button
                          onClick={() => handleTriggerUpload(item.id)}
                          className="text-stone-400 hover:text-brand-500 flex flex-col items-center justify-center w-full h-full transition-colors"
                          title="Görsel Yükle"
                        >
                          <Upload className="w-4 h-4" />
                        </button>
                      )
                    )}
                  </div>
                </td>
                <td className="py-2 px-3 font-semibold text-stone-700">
                  <select
                    value={item.category}
                    disabled={readOnlyState.isReadOnly}
                    onChange={e => handleUpdateSpecItem(item.id, { category: e.target.value })}
                    className="bg-transparent hover:bg-stone-100/80 px-2 py-1 rounded border border-transparent hover:border-stone-200 focus:outline-none focus:bg-white focus:border-stone-300 font-semibold text-stone-700 text-xs cursor-pointer max-w-[140px] disabled:bg-transparent disabled:cursor-not-allowed"
                  >
                    {['Aksesuar', 'Aydınlatma', 'Kulp', 'Tezgâh', 'Renk', 'Gövde Rengi', 'Cihaz', 'Eviye', 'Armatür'].map(cat => (
                      <option key={cat} value={cat}>{cat === 'Renk' ? 'Kapak Rengi' : cat}</option>
                    ))}
                  </select>
                </td>
                <td className="py-2 px-3">
                  <input 
                    type="text" 
                    value={item.code} 
                    disabled={readOnlyState.isReadOnly}
                    onChange={e => handleUpdateSpecItem(item.id, { code: e.target.value })}
                    className="bg-transparent hover:bg-stone-100/80 px-2 py-1 rounded border border-transparent hover:border-stone-200 focus:outline-none focus:bg-white focus:border-stone-300 font-mono font-bold text-stone-800 text-xs w-full disabled:cursor-not-allowed"
                  />
                </td>
                <td className="py-2 px-3">
                  <input 
                    type="text" 
                    value={item.name}
                    disabled={readOnlyState.isReadOnly}
                    onChange={(e) => handleUpdateSpecItem(item.id, { name: e.target.value })}
                    className="w-full px-2 py-1 border border-stone-200 rounded text-xs focus:outline-none focus:border-brand-500 bg-stone-50/50 disabled:bg-stone-100 disabled:cursor-not-allowed font-medium text-stone-800"
                    placeholder="Kalem açıklamasını giriniz..."
                  />
                </td>
                <td className="py-2 px-3">
                  <input
                    type="number"
                    min={1}
                    value={item.quantity ?? ''}
                    placeholder="-"
                    disabled={readOnlyState.isReadOnly}
                    onChange={e => {
                      const parsed = parseInt(e.target.value, 10);
                      handleUpdateSpecItem(item.id, { quantity: e.target.value === '' || isNaN(parsed) ? undefined : parsed });
                    }}
                    className="w-14 text-center px-1 py-1 border border-stone-200 rounded text-xs focus:outline-none focus:border-brand-500 bg-stone-50/50 disabled:bg-stone-100 disabled:cursor-not-allowed tabular-nums placeholder:text-stone-300"
                  />
                </td>
                <td className="py-2 px-3 text-center">
                  {!readOnlyState.isReadOnly && (
                    <button 
                      onClick={() => handleRemoveSpecItem(item.id)}
                      className="p-1 text-stone-400 hover:text-red-500 rounded transition-colors"
                      title="Kalemi Sil"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {specItems.length === 0 && (
              <tr>
                <td colSpan={7} className="py-8 text-center text-stone-400">
                  Henüz teknik ürün detayı eklenmedi. Arama çubuğunu kullanın veya manuel satır ekleyin.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
